import { cn } from '@/lib/utils'

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Variant of the skeleton animation */
  variant?: 'shimmer' | 'pulse'
}

/**
 * Skeleton component following Fluent 2 Wait-UX patterns.
 * Used to indicate loading states with accessible motion.
 */
function Skeleton({
  className,
  variant = 'shimmer',
  ...props
}: SkeletonProps) {
  return (
    <div
      className={cn(
        'rounded-md bg-[#f5f5f5] dark:bg-[#3d3d3d]',
        variant === 'shimmer' && 'skeleton',
        variant === 'pulse' && 'animate-fluent-pulse',
        className
      )}
      {...props}
    />
  )
}

/**
 * Text skeleton with proper height for text lines.
 */
function SkeletonText({
  className,
  lines = 1,
  ...props
}: SkeletonProps & { lines?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === lines - 1 && lines > 1 && 'w-3/4',
            className
          )}
          {...props}
        />
      ))}
    </div>
  )
}

/**
 * Avatar skeleton with circular shape.
 */
function SkeletonAvatar({
  className,
  size = 'md',
  ...props
}: SkeletonProps & { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  }

  return (
    <Skeleton
      className={cn('rounded-full', sizeClasses[size], className)}
      {...props}
    />
  )
}

/**
 * Card skeleton with header, content, and optional footer.
 */
function SkeletonCard({
  className,
  showHeader = true,
  showFooter = false,
  ...props
}: SkeletonProps & { showHeader?: boolean; showFooter?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-xl bg-white dark:bg-[#292929] p-6 shadow-fluent-4',
        className
      )}
      {...props}
    >
      {showHeader && (
        <div className="flex items-center gap-4 mb-4">
          <SkeletonAvatar />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      )}
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
      {showFooter && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e5e5e5] dark:border-[#3d3d3d]">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
      )}
    </div>
  )
}

/**
 * Table row skeleton.
 */
function SkeletonTableRow({
  className,
  columns = 4,
  ...props
}: SkeletonProps & { columns?: number }) {
  return (
    <div
      className={cn('flex items-center gap-4 py-4', className)}
      {...props}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            i === 0 ? 'w-1/6' : 'flex-1'
          )}
        />
      ))}
    </div>
  )
}

export { Skeleton, SkeletonText, SkeletonAvatar, SkeletonCard, SkeletonTableRow }
